import { useForm } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArticleNotificationReach,
  Notification,
  NotificationMethod,
  RecipientGroup,
  CompanyType,
} from "@/types";
import {
  createArticleNotification,
  getArticleNotificationReach,
  useCompaniesNames,
  useFullArticle,
} from "@/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { numberToRecipientGroup, recipientGroupToChoiceNumber } from "@/lib";
import {
  Breadcrumbs,
  Button,
  Dropdown,
  Icon,
  Option,
  ProgressIndicator,
  SelectRow,
} from "@app-components";
import PageBackButton from "@/components/PageBackButton";
import { useWelcomeCommunicationAccess } from "@/hooks/useWelcomeCommunicationAccess";

export const Route = createFileRoute(
  "/_portal/building/communication/new-notification/$articleId",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { articleId } = Route.useParams();
  const { data: article } = useFullArticle(articleId);
  const { data: companies } = useCompaniesNames(
    article.recipients.companiesIds,
    {
      getAllIfNoCompanyIds: true,
      noExternal: true,
    },
  );
  const { hasCommunication, visibleForUsers } = useWelcomeCommunicationAccess();
  const [reach, setReach] = useState<ArticleNotificationReach | null>(null);
  const [reachError, setReachError] = useState(false);
  const reachRequestId = useRef(0);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const form = useForm<Notification>({
    defaultValues: {
      notificationMethods: [NotificationMethod.EMAIL],
      recipients: {
        companiesIds: article.recipients.companiesIds ?? ["__ALL__"],
        groups: article.recipients.groups ?? [],
      },
    },
    onSubmit: async ({ value }) => {
      const normalizedCompaniesIds = value.recipients.companiesIds.includes(
        "__ALL__",
      )
        ? [] // backend will treat [] as all recipient companies
        : value.recipients.companiesIds;
      await createArticleNotification(
        articleId,
        value.recipients.groups,
        normalizedCompaniesIds,
        value.notificationMethods,
      );
      navigate({
        to: `/building/communication/$articleId`,
        params: { articleId },
      });
    },
  });

  const breadcrumbs = [
    {
      label: t("COMMUNICATION.HEADER"),
      path: `/building/communication`,
    },
    {
      label: `${i18n.language === "en" ? article.title.en : article.title.no}`,
      path: `/building/communication/${articleId}`,
    },
    {
      label: t("COMMUNICATION.NEW_NOTIFICATION"),
      path: Route.path,
    },
  ];

  const handleReachStatistics = async () => {
    // Ignore responses from superseded requests so a stale result can't win
    const requestId = ++reachRequestId.current;
    setReachError(false);
    setReach(null);
    const { companiesIds, groups } = form.state.values.recipients;
    try {
      const stats = await getArticleNotificationReach(
        companiesIds.length === 0 || companiesIds.includes("__ALL__")
          ? (article?.recipients?.companiesIds ?? [])
          : companiesIds,
        groups,
        article.disregardConsent,
      );
      if (requestId === reachRequestId.current) setReach(stats);
    } catch (error) {
      console.error("Failed to get article notification reach", error);
      if (requestId === reachRequestId.current) setReachError(true);
    }
  };

  const hasRecipients = !!reach?.userCount || !!reach?.companyAdminCount;

  useEffect(() => {
    handleReachStatistics();
  }, []);

  const recipientCompanyDropdownOptions = useMemo<Option[]>(() => {
    if (!companies) return [];
    return companies
      .filter((company) => company.companyType !== CompanyType.EXTERNAL)
      .map(({ buildingTenantId, buildingTenantName }) =>
        buildingTenantId && buildingTenantName
          ? {
              value: buildingTenantId,
              label: buildingTenantName,
            }
          : undefined,
      )
      .filter((v) => !!v);
  }, [article.recipients.companiesIds, companies]);

  // const notificationMethodOptions = useMemo(
  //   () => [
  //     {
  //       value: 1,
  //       label: t("ARTICLE.NOTIFICATION_METHODS.EMAIL"),
  //     },
  //     // TODO: reinstate once SMS notifications are implemented
  //     /* {
  //       value: 2,
  //       label: t("ARTICLE.NOTIFICATION_METHODS.SMS"),
  //     },
  //     {
  //       value: 3,
  //       label: t("ARTICLE.NOTIFICATION_METHODS.EMAIL_AND_SMS"),
  //     }, */
  //   ],
  //   [],
  // );
  const notificationGroupOptions = useMemo(
    () => [
      {
        value: 0,
        label: t("ARTICLE.NOTIFICATION_GROUPS.ALL_USERS"),
      },
      {
        value: 1,
        label: t("ARTICLE.NOTIFICATION_GROUPS.ONLY_ADMINS"),
      },
    ],
    [],
  );
  // const notificationMethodToNumber = (methods: NotificationMethod[]) => {
  //   if (methods.includes(NotificationMethod.EMAIL)) {
  //     if (methods.includes(NotificationMethod.SMS)) {
  //       return 3;
  //     }
  //     return 1;
  //   }
  //   if (methods.includes(NotificationMethod.SMS)) {
  //     return 2;
  //   }
  //   return 0;
  // };
  // const numberToNotificationMethod = (choice: number | undefined) => {
  //   if (!choice) return [];
  //   if (choice === 1) {
  //     return [NotificationMethod.EMAIL];
  //   } else if (choice == 2) {
  //     return [NotificationMethod.SMS];
  //   } else if (choice == 3) {
  //     return [NotificationMethod.EMAIL, NotificationMethod.SMS];
  //   } else {
  //     return [];
  //   }
  // };

  if (!hasCommunication) return null;
  return (
    <div>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton
          fallbackPath={{
            to: "/building/communication/$articleId",
            params: { articleId },
          }}
        />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      {!visibleForUsers && (
        <p
          className="banner warningBanner"
          style={{ marginBottom: "var(--wlcm-spacing-sm)" }}
        >
          <Icon name="warning" />{" "}
          {t("COMMUNICATION.HIDDEN_FOR_USERS_NOTIFICATION_INFO")}
        </p>
      )}
      <div className="pageHeader">
        <h1>{t("COMMUNICATION.NEW_NOTIFICATION")}</h1>
        <p>
          For {t("COMMUNICATION.NEWS").toLowerCase()}:{" "}
          <b>{article.title[i18n.language === "en" ? "en" : "no"]}</b>
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        style={{
          marginBottom: "var(--wlcm-spacing-md)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--wlcm-spacing-sm)",
        }}
      >
        {/* <form.Field name="notificationMethods"> */}
        {/*   {(field) => ( */}
        {/*     <SelectRow */}
        {/*       label={t("ARTICLE.NOTIFICATION_METHOD")} */}
        {/*       name="notification-method-input" */}
        {/*       value={notificationMethodToNumber(field.state.value)} */}
        {/*       options={notificationMethodOptions} */}
        {/*       onBlur={field.handleBlur} */}
        {/*       onChange={(e) => { */}
        {/*         const methods = numberToNotificationMethod(e as number); */}
        {/*         field.handleChange(methods); */}
        {/*       }} */}
        {/*     /> */}
        {/*   )} */}
        {/* </form.Field> */}
        <form.Field name="recipients.companiesIds">
          {(field) => {
            const currentValue: Option[] = [
              ...(field.state.value?.includes("__ALL__")
                ? [
                    {
                      label: t("ARTICLE.ALL_RECIPIENT_COMPANIES"),
                      value: "__ALL__",
                    },
                  ]
                : []),
              ...recipientCompanyDropdownOptions.filter((opt) =>
                form.state.values.recipients.companiesIds?.includes(opt.value),
              ),
            ];
            return (
              <Dropdown
                compact
                isMulti
                label={t("ARTICLE.NOTIFICATION_COMPANIES")}
                placeholder={t("ARTICLE.ALL_RECIPIENT_COMPANIES")}
                onBlur={field.handleBlur}
                value={currentValue}
                onChange={async (opts: Option[] | undefined) => {
                  field.handleChange(opts?.map((opt) => opt.value) ?? []);
                  handleReachStatistics();
                }}
                options={recipientCompanyDropdownOptions}
                showSelectAllOption
                selectAllLabel={t("ARTICLE.ALL_RECIPIENT_COMPANIES")}
                style={{ marginTop: "var(--wlcm-spacing-sm)" }}
                disabled={article.recipients?.companiesIds?.length === 1}
                infoText={
                  article.recipients?.companiesIds?.length === 1
                    ? t("ARTICLE.SINGLE_RECIPIENT_INFO")
                    : undefined
                }
              />
            );
          }}
        </form.Field>

        <form.Field name="recipients.groups">
          {(field) => {
            let value = 0;
            if (field.state.value && field.state.value[0])
              value = recipientGroupToChoiceNumber(field.state.value[0]);
            return (
              <SelectRow
                label={t("ARTICLE.NOTIFICATION_GROUP")}
                value={value}
                name="recipient-group-input"
                options={notificationGroupOptions}
                onBlur={field.handleBlur}
                onChange={(e) => {
                  field.handleChange([numberToRecipientGroup(e as number)]);
                  handleReachStatistics();
                }}
              />
            );
          }}
        </form.Field>
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <>
              {reachError ? (
                <p className="banner warningBanner">
                  <Icon name="warning" /> {t("ARTICLE.REACH_ERROR")}{" "}
                  <Button type="button" onClick={handleReachStatistics}>
                    {t("ARTICLE.REACH_RETRY")}
                  </Button>
                </p>
              ) : !reach ? (
                <p>{t("ARTICLE.REACH_LOADING")}</p>
              ) : (
                !hasRecipients && (
                  <p className="banner warningBanner">
                    <Icon name="info" /> {t("ARTICLE.NO_RECIPIENTS")}
                  </p>
                )
              )}
              <Button
                success
                type="submit"
                icon="send"
                disabled={!canSubmit || !hasRecipients}
                isLoading={isSubmitting}
                style={{
                  marginLeft: "auto",
                }}
              >
                {t("COMMUNICATION.SEND_NOTIFICATION")}
              </Button>
            </>
          )}
        />
      </form>
      <ProgressIndicator
        id="notificationReach"
        total={article.statistics?.reach.recipients.total}
        values={[
          {
            id: "cannotBeNotified",
            value: reach?.usersWithoutConsentCount ?? 0,
            color: "var(--wlcm-color-warning-orange)",
            label: t("COMMUNICATION.CANNOT_BE_NOTIFIED").toLowerCase(),
            includeInTotal: false,
          },
          {
            id: "companyAdmins",
            value: reach?.companyAdminCount ?? 0,
            color: "var(--wlcm-color-blue)",
            label: t("COMMUNICATION.COMPANY_ADMINS").toLowerCase(),
          },
          {
            id: "users",
            value:
              form.state.values.recipients.groups.includes(
                RecipientGroup.ALL,
              ) || form.state.values.recipients.groups.length === 0
                ? (reach?.userCount ?? 0)
                : 0,
            color: "var(--wlcm-color-very-light-blue)",
            label: t("COMMUNICATION.REGULAR_USERS").toLowerCase(),
          },
        ]}
        label={(data) => (
          <Trans
            i18nKey="COMMUNICATION.NOTIFICATION_STATS"
            values={data}
            components={{
              strong: <strong />,
              colored: (
                <span
                  style={{
                    color: "var(--wlcm-color-blue)",
                  }}
                />
              ),
              grey: <span style={{ opacity: 0.6 }} />,
            }}
          />
        )}
        restCountLabel={t("COMMUNICATION.NON_RECIPIENTS").toLowerCase()}
      />
      {!!reach?.usersWithoutConsentCount && (
        <p
          className="banner warningBanner"
          style={{ marginTop: "var(--wlcm-spacing-sm)" }}
        >
          <Icon name="info" />
          {t("ARTICLE.CANNOT_BE_NOTIFIED", {
            count: reach.usersWithoutConsentCount,
          })}
        </p>
      )}
    </div>
  );
}
